import { useRef, useState } from 'react'
import { UploadCloud, X, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useUploadImage } from '@/application/services/upload.service'
import { Button } from '@/ui/components/common/Button'
import { Input } from '@/ui/components/forms/Input'
import { classNames } from '@/ui/utils/main'

interface ImageFieldProps {
  value: string
  onChange: (url: string) => void
  disabled?: boolean
}

export function ImageField({ value, onChange, disabled }: ImageFieldProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const { mutateAsync: uploadImage, isPending } = useUploadImage()

  const handleFile = async (file: File) => {
    setUploadError(null)
    try {
      const imageUrl = await uploadImage(file)
      onChange(imageUrl)
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'Upload failed')
    }
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files[0]
    if (file) handleFile(file)
  }

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) handleFile(file)
    event.target.value = '' // reset so the same file can be re-selected
  }

  const isInteractive = !disabled && !isPending

  return (
    <div className="space-y-3">
      {/* ── Drop zone / preview ──────────────────────────────── */}
      <div
        role="button"
        tabIndex={isInteractive ? 0 : -1}
        aria-label={t('admin.image_upload_aria')}
        className={classNames(
          'relative w-full rounded-lg border-2 border-dashed transition-colors overflow-hidden',
          isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50',
          isInteractive ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
          value ? 'aspect-video' : 'h-36',
        )}
        onClick={() => isInteractive && inputRef.current?.click()}
        onKeyDown={(event) => event.key === 'Enter' && isInteractive && inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault()
          if (isInteractive) setIsDragging(true)
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={isInteractive ? handleDrop : undefined}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="Cover preview"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Hover overlay — "click to replace" hint */}
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
              <UploadCloud className="w-6 h-6 text-white" />
              <span className="text-white text-sm font-medium">
                {t('admin.image_click_replace')}
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground p-4 text-center">
            <UploadCloud className="w-8 h-8 opacity-50" />
            <p className="text-sm font-medium">{t('admin.image_drop_prompt')}</p>
            <p className="text-xs opacity-70">{t('admin.image_file_hint')}</p>
          </div>
        )}

        {/* Upload spinner */}
        {isPending && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-sm">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Upload error */}
      {uploadError && <p className="text-destructive text-xs">{uploadError}</p>}

      {/* Remove button (only when an image is set) */}
      {value && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!isInteractive}
          onClick={() => {
            onChange('')
            setUploadError(null)
          }}
          leftIcon={<X className="w-3 h-3" />}
        >
          {t('admin.image_remove')}
        </Button>
      )}

      {/* ── "or enter a URL" separator + input ─────────────── */}
      <div className="flex items-center gap-3">
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground shrink-0">
          {t('admin.image_url_separator')}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      <Input
        type="text"
        placeholder="https://example.com/cover.jpg"
        value={value}
        onChange={(event) => {
          onChange(event.target.value)
          setUploadError(null)
        }}
        disabled={!isInteractive}
      />

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileInput}
        disabled={!isInteractive}
      />
    </div>
  )
}
